import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ConsentModal from '../components/ConsentModal.js';
import { useLocalizedNavigate } from '../utils/localizedPath.js';

function ViewMatch() {
  const [searchParams] = useSearchParams();
  const navigate = useLocalizedNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentProcessing, setConsentProcessing] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing token');
      setLoading(false);
      return;
    }

    fetchMatchData();
  }, [token]);

  const fetchMatchData = async () => {
    try {
      const response = await fetch(`/api/view-match?token=${token}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load assignment');
      }

      const data = await response.json();
      setMatchData(data);
      setLoading(false);

      // Show consent modal after 3-4 seconds if consent not already given
      if (!data.consentStatus.consentGiven && !data.consentStatus.unsubscribed) {
        setTimeout(() => {
          setShowConsentModal(true);
        }, 3500); // 3.5 seconds
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleConsentDecision = async (consentGiven) => {
    setConsentProcessing(true);

    try {
      const response = await fetch('/api/consent/record', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          consentGiven,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to record consent');
      }

      setShowConsentModal(false);
      setConsentProcessing(false);

      // If consent given, redirect to preferences survey
      if (consentGiven) {
        navigate(`/gift-preferences?token=${token}`);
      }
    } catch (err) {
      console.error('Error recording consent:', err);
      setConsentProcessing(false);
      alert('Failed to record your response. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
          <p className="text-gray-700 text-lg">Loading your assignment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Oops!</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            Your link may have expired or is invalid. Please check your email for a new link.
          </p>
        </div>
      </div>
    );
  }

  const firstRecipient = matchData?.assignments?.[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎅</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Hi {matchData?.participant.name}!
          </h1>
          <p className="text-xl text-gray-600">
            Your {matchData?.event.name} Assignment
          </p>
        </div>

        {/* Event Details */}
        <div className="bg-white rounded-lg shadow-xl p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Event Details</h2>
          <div className="space-y-2">
            {matchData?.event.eventDate && (
              <p className="text-gray-700">
                <strong>Date:</strong> {new Date(matchData.event.eventDate).toLocaleDateString()}
              </p>
            )}
            {matchData?.event.budget && (
              <p className="text-gray-700">
                <strong>Budget:</strong> {matchData.event.budget}
              </p>
            )}
            {matchData?.event.message && (
              <p className="text-gray-700 mt-4 p-4 bg-gray-50 rounded border-l-4 border-green-600">
                {matchData.event.message}
              </p>
            )}
          </div>
        </div>

        {/* Assignment Card */}
        <div className="bg-gradient-to-r from-green-500 to-red-500 rounded-lg shadow-2xl p-1 mb-6">
          <div className="bg-white rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              You're buying gift(s) for:
            </h2>

            {matchData?.assignments && matchData.assignments.length > 0 ? (
              <div className="space-y-4">
                {matchData.assignments.map((recipient, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-red-50 to-green-50 rounded-lg p-6 border-2 border-green-600"
                  >
                    <h3 className="text-3xl font-bold text-green-700 mb-3 text-center">
                      🎁 {recipient.name}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                      {recipient.ageRange && (
                        <div>
                          <strong>Age:</strong> {recipient.ageRange}
                        </div>
                      )}
                      {recipient.sex && recipient.sex !== 'Unknown' && (
                        <div>
                          <strong>Gender:</strong> {recipient.sex}
                        </div>
                      )}
                    </div>

                    {recipient.preferences && (
                      <div className="mt-4 p-4 bg-white rounded border-l-4 border-yellow-400">
                        <strong className="text-gray-800">Gift Preferences:</strong>
                        <p className="text-gray-700 mt-2">{recipient.preferences}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-600">
                No assignments found. Please contact the event organizer.
              </p>
            )}
          </div>
        </div>

        {/* Happy Gifting Message */}
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800">Happy Gifting! 🎄</p>
        </div>
      </div>

      {/* Consent Modal */}
      {!consentProcessing && (
        <ConsentModal
          isOpen={showConsentModal}
          onAccept={() => handleConsentDecision(true)}
          onDecline={() => handleConsentDecision(false)}
          recipientName={firstRecipient?.name}
          onClose={() => setShowConsentModal(false)}
        />
      )}

      {/* Processing Overlay */}
      {consentProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-700">Processing...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewMatch;
